import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../../../styles/pages/about/Pipeline3D.module.scss";


const defaultStages = [
  {
    key: "social-engineering-attacks ",
    title: "Social Engineering Attacks ",
    blurb: "Social engineering is the fastest way around good security, the human element shows up in 68% of breaches. Worldwide, business email compromise scams drove $2.9B in reported losses (2023), often targeting finance, HR, and operations.",
    chips: ["Phishing", "Pretexting"],
    gradient: "linear-gradient(135deg, #60a5fa, #22d3ee)",
    visual: "slide-one",
  },
  {
    key: "ransomware",
    title: "Ransomware",
    blurb: "Ransomware surged dramatically in the 2010s, affecting many countries worldwide, including the United States, the United Kingdom, Canada, Australia, India, and others. Visit cisa.gov/stopransomware before you travel to stay informed and reduce your risk.",
    chips: ["Extortion", "Malware"],
    gradient: "linear-gradient(135deg, #8b5cf6, #60a5fa)",
    visual: "slide-two",
  },
  {
    key: "denial-of-service",
    title: "DoS (denial of service)",
    blurb: "With advances in technology, cyber predators have gained more ways to target businesses online, through phishing, account takeovers, and fraud. Worldwide, the impact is severe, Verizon’s 2024 DBIR reviewed 30,458 incidents and 10,626 confirmed breaches across 94 countries.",
    chips: ["DoS", "DDoS"],
    gradient: "linear-gradient(135deg, #f59e0b, #f43f5e)",
    visual: "DoS",
  },
];

export default function Pipeline3D({
  title = "From Idea → Concept → Design",
  stages = defaultStages,
  auto = true,
  intervalMs = 3600,
  className = "",
}) {
  const cardRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);

  // Respect prefers-reduced-motion
  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  const effectiveAuto = auto && !prefersReducedMotion;

  // Autoplay
  useEffect(() => {
    if (!effectiveAuto || !playing) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % stages.length), intervalMs);
    return () => clearInterval(t);
  }, [effectiveAuto, playing, intervalMs, stages.length]);

  // Parallax tilt (mild)
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    let rect = el.getBoundingClientRect();

    const tilt = (e) => {
      const cx = (e.clientX ?? (e.touches?.[0]?.clientX || rect.left)) - rect.left;
      const cy = (e.clientY ?? (e.touches?.[0]?.clientY || rect.top)) - rect.top;
      const rx = ((cy / rect.height) - 0.5) * -6;
      const ry = ((cx / rect.width) - 0.5) * 8;
      el.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg)`;
    };
    const reset = () => { el.style.transform = "rotateX(0deg) rotateY(0deg)"; };
    const onResize = () => { rect = el.getBoundingClientRect(); };

    el.addEventListener("mousemove", tilt);
    el.addEventListener("mouseleave", reset);
    el.addEventListener("touchmove", tilt, { passive: true });
    el.addEventListener("touchend", reset);
    window.addEventListener("resize", onResize);

    return () => {
      el.removeEventListener("mousemove", tilt);
      el.removeEventListener("mouseleave", reset);
      el.removeEventListener("touchmove", tilt);
      el.removeEventListener("touchend", reset);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // Keyboard controls
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space") { e.preventDefault(); setPlaying((p) => !p); }
      if (e.code === "ArrowRight") { setPlaying(false); setIdx((i) => (i + 1) % stages.length); }
      if (e.code === "ArrowLeft") { setPlaying(false); setIdx((i) => (i - 1 + stages.length) % stages.length); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stages.length]);

  const progressPct = ((idx + 1) / stages.length) * 100;

  const jump = (i) => { setPlaying(false); setIdx(i); };
  const next = () => { setPlaying(false); setIdx((i) => (i + 1) % stages.length); };
  const prev = () => { setPlaying(false); setIdx((i) => (i - 1 + stages.length) % stages.length); };

  return (
    <section className={`${styles.root} ${className}`}>
      <div className={styles.frame}>
        <div ref={cardRef} className={styles.card} aria-label="3D pipeline: Idea to Concept to Design">
          <div className={styles.softglow} aria-hidden />
          <div className={styles.floor} aria-hidden />

          <div className={styles.header}>
            <div>
              <span className={styles.kicker}>cyber</span>
              {/* <div className={styles.title}>{title}</div> */}
            </div>
            <div className={styles.controls}>
              <button className={styles.btn} onClick={prev} aria-label="Previous">◀︎</button>
              <button className={styles.btn} onClick={() => setPlaying((p) => !p)} aria-label={playing ? "Pause" : "Play"}>
                {playing ? "⏸" : "▶︎"}
              </button>
              <button className={styles.btn} onClick={next} aria-label="Next">▶︎</button>
            </div>
          </div>

          <div className={styles.pipeline} aria-live="polite">
            {stages.map((s, i) => {
              const rel = (i - idx + stages.length) % stages.length;
              const slotClass = rel === 0 ? styles.slotCenter : rel === 1 ? styles.slotRight : styles.slotLeft;
              return (
                <article key={s.key} className={`${styles.stage} ${slotClass}`} style={{ "--grad": s.gradient }}>
                  <div className={styles.copy}>
                    <div className={styles.stageheader}>
                      <div className={styles.badge} aria-hidden />
                      <h3>{s.title}</h3>
                    </div>
                    <p>{s.blurb}</p>
                    <div className={styles.chiprow}>
                      {(s.chips || []).slice(0, 2).map((c, j) => (
                        <span key={j} className={styles.chip}>{c}</span>
                      ))}
                    </div>
                  </div>
                  <div className={styles.anim} aria-hidden>
                    {typeof s.visual === "string" ? (
                      s.visual === "slide-one" ? <SlideOne /> : s.visual === "slide-two" ? <SlideTwo /> : <VisualDesign />
                    ) : (
                      s.visual || null
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          <div className={styles.rail}><div className={styles.progress} style={{ width: `${progressPct}%` }} /></div>
          <div className={styles.legend}>
            {stages.map((s, i) => (
              <button key={s.key} onClick={() => jump(i)} className={styles.legendBtn} aria-label={`Go to ${s.title}`}>
                <span className={`${styles.dot} ${i===0?styles.dotI:i===1?styles.dotC:styles.dotD}`}></span>
                {s.title}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------- Inline SVG Visuals (lightweight) ------------- */
function SlideOne(){
  return (
    <svg viewBox="0 0 280 180" xmlns="http://www.w3.org/2000/svg" className={styles.svg}>
      <defs>
        <linearGradient id="gBrain" x1="0" x2="1">
          <stop offset="0" stopColor="#60a5fa"/>
          <stop offset="1" stopColor="#22d3ee"/>
        </linearGradient>
      </defs>
      {/* Brain outline that draws in */}
      <g fill="none" stroke="url(#gBrain)" strokeWidth="2" opacity=".95">
        <path
          d="M70 120c-16 0-28-11-28-26 0-13 11-24 24-24 3-11 14-20 28-20 12 0 21 6 25 14 5-7 13-10 21-10 15 0 26 8 26 20 13 0 22 10 22 22 0 17-14 30-30 30"
          className={styles.brainOutline}
        />
      </g>
      {/* Pulsing thought nodes */}
      <g fill="#93c5fd">
        <circle cx="96" cy="70" r="3" className={styles.pulseSmall} style={{animationDelay:"0s"}}/>
        <circle cx="138" cy="66" r="3" className={styles.pulseSmall} style={{animationDelay:".2s"}}/>
        <circle cx="120" cy="104" r="3" className={styles.pulseSmall} style={{animationDelay:".4s"}}/>
        <circle cx="164" cy="96" r="3" className={styles.pulseSmall} style={{animationDelay:".6s"}}/>
      </g>
      {/* Synapse arcs (subtle motion) */}
      <g fill="none" stroke="url(#gBrain)" strokeWidth="1.5" className={styles.synapse}>
        <path d="M96 70 C110 82 124 86 138 66"/>
        <path d="M120 104 C132 92 146 88 164 96"/>
      </g>
      {/* Tiny orbit around a node */}
      <g className={styles.spin} style={{transformOrigin:"138px 66px"}}>
        <circle cx="138" cy="66" r="10" fill="none" stroke="rgba(148,163,184,.25)" />
        <circle cx="148" cy="66" r="2" fill="#e0f2fe" />
      </g>
    </svg>
  );
}

function SlideTwo(){
  return (
    <svg viewBox="0 0 280 180" xmlns="http://www.w3.org/2000/svg" className={styles.svg}>
      <defs>
        <linearGradient id="g2" x1="0" x2="1">
          <stop offset="0" stopColor="#a78bfa"/><stop offset="1" stopColor="#60a5fa"/>
        </linearGradient>
      </defs>
      <g opacity=".95">
        <rect x="20"  y="30" width="90" height="60" rx="8" fill="rgba(148,163,184,.12)" stroke="rgba(255,255,255,.18)"/>
        <rect x="170" y="90" width="90" height="60" rx="8" fill="rgba(148,163,184,.12)" stroke="rgba(255,255,255,.18)"/>
        <rect x="100" y="70" width="90" height="60" rx="8" fill="rgba(148,163,184,.12)" stroke="rgba(255,255,255,.18)"/>
      </g>
      <g>
        <rect x="28" y="42" width="74" height="8" rx="4" fill="url(#g2)" className={styles.shimmer}/>
      </g>
      <g fill="none" stroke="url(#g2)" strokeWidth="2" strokeLinecap="round" strokeDasharray="6 6">
        <path d="M65 90 C120 120 160 40 200 120" />
      </g>
    </svg>
  );
}

function VisualDesign(){
  return (
    <svg viewBox="0 0 280 180" xmlns="http://www.w3.org/2000/svg" className={styles.svg}>
      <defs>
        <linearGradient id="gProg" x1="0" x2="1">
          <stop offset="0" stopColor="#f59e0b"/>
          <stop offset="1" stopColor="#f43f5e"/>
        </linearGradient>
      </defs>
      {/* Canvas */}
      <rect x="16" y="24" width="248" height="132" rx="12" fill="rgba(148,163,184,.10)" stroke="rgba(255,255,255,.18)"/>
      {/* Progressive lines */}
      <path id="p1" d="M28 140 C 80 90 132 120 200 60" stroke="url(#gProg)" className={styles.draw}/>
      <path id="p2" d="M36 126 C 96 84 144 132 232 86" stroke="url(#gProg)" className={styles.draw} style={{animationDelay:'.3s'}}/>
      {/* Traveling dots */}
      <g fill="#fff">
        <circle r="4">
          <animateMotion dur="2.2s" repeatCount="indefinite">
            <mpath xlinkHref="#p1" />
          </animateMotion>
        </circle>
        <circle r="3" opacity=".9">
          <animateMotion dur="2.0s" begin=".3s" repeatCount="indefinite">
            <mpath xlinkHref="#p2" />
          </animateMotion>
        </circle>
      </g>
    </svg>
  );
}

/* =============================
   FILE: Pipeline3D.module.scss
============================= */
